import { useEffect, useState } from 'react'
import { apiGet } from '../api/client'
import { useSupabaseToken } from './useSupabaseToken'
import { configureRc, getSubscriptionSummary } from '../lib/revenuecat'

/**
 * Why an outcome and not just a null URL: "RevenueCat answered and this
 * customer has no subscription to manage" and "the lookup failed" are the SAME
 * null, and they warrant opposite copy. NEITHER of them licenses naming the
 * App Store, which is what this surface used to do. RevenueCat's own SDK
 * documents managementURL as the App Store's URL for an App Store
 * subscription, so a null can never mean "billed by Apple"; it means there is
 * no subscription RevenueCat can see, which is exactly what a comped grant, a
 * lapsed sub, or a blocked billing SDK looks like.
 */
export type ManagementOutcome = 'pending' | 'ok' | 'none' | 'error'

export interface SubscriptionSummaryState {
  /** RevenueCat's store-correct management URL, or null when there is none. */
  url: string | null
  /** False until the lookup has settled. Callers render nothing before this. */
  resolved: boolean
  /** How the lookup ended. Distinguishes "no portal" from "could not ask". */
  outcome: ManagementOutcome
  /**
   * The store this subscription is billed by, when the SERVER told us
   * ('rc_billing', 'app_store', 'promotional', ...). Null means unknown, which
   * is the only honest answer from the browser SDK alone. Copy may name a
   * platform ONLY when this is set.
   */
  store: string | null
  /**
   * Whether the subscription renews at the end of the period. NULL is UNKNOWN
   * (no `pro` entitlement, or the lookup failed), never "will not renew".
   */
  willRenew: boolean | null
  /** Inside a free trial. NULL is unknown. Changes what the date MEANS. */
  isTrial: boolean | null
}

interface ServerManageUrl {
  url: string | null
  store: string | null
  // 'unconfigured' means the server has no RevenueCat secret key and never
  // asked, which is a fall-back-to-the-SDK instruction, not a failure.
  outcome: 'ok' | 'none' | 'unconfigured' | 'error'
}

/**
 * Resolve RevenueCat's subscription state for a signed-in customer: where they
 * cancel, and whether the subscription renews.
 *
 * ONE implementation, used by both the paywall's already-subscribed row and
 * the account page's Subscription card. These must never diverge: they are the
 * two places a customer is told how to cancel, and the Terms of Service commit
 * to the account-page one in writing.
 *
 * TWO sources, deliberately, and the server wins:
 *
 *   - `GET /pro/manage-url` mints a PRE-AUTHORIZED portal link through
 *     RevenueCat's v2 API. The browser SDK cannot do this (it needs a secret
 *     key), and its unauthenticated URL drops a customer on a "check your
 *     email for a login link" page - after they already signed in with us,
 *     possibly with Google, which has no email login at all. That page is what
 *     our own cancel flow served on 2026-08-24.
 *   - The SDK still answers `willRenew` / `isTrial`, which the server endpoint
 *     does not carry, and still provides the management URL when the server is
 *     unconfigured or unreachable.
 *
 * Both run concurrently and neither can fail the other: a rejected half
 * degrades to unknown rather than taking the whole card down.
 *
 * It configures the SDK ITSELF rather than relying on a parent effect. React
 * runs a child's effects BEFORE its parent's on mount, so without this the
 * getCustomerInfo call can hit an unconfigured SDK, throw
 * UninitializedPurchasesError, and show a web-billed subscriber the fallback
 * copy on the first render of a session and the real link on every later one.
 * configureRc is idempotent and serialized, so calling it here costs nothing.
 *
 * Never throws: an account page must not break on a billing lookup.
 */
export function useSubscriptionSummary(userId: string | null): SubscriptionSummaryState {
  const { token } = useSupabaseToken()
  // ONE state object, stamped with the user it describes. Cross-account
  // staleness is then impossible by construction rather than by remembering
  // to clear five fields on every identity change: a result belonging to
  // another user can never be read, because `belongsTo` will not match. The
  // previous version cleared them imperatively, which also meant calling
  // setState synchronously inside the effect on every render path.
  const [state, setState] = useState<Resolved>(UNRESOLVED)

  useEffect(() => {
    if (!userId) return
    let cancelled = false

    void (async () => {
      const [server, sdk] = await Promise.all([
        fetchServerManageUrl(token),
        fetchSdkSummary(userId),
      ])
      if (cancelled) return

      // Renewal wording only ever comes from the SDK; the server endpoint
      // deliberately carries no entitlement fields.
      const willRenew = sdk.ok ? sdk.willRenew : null
      const isTrial = sdk.ok ? sdk.isTrial : null

      // The server asked RevenueCat's API directly with a secret key, so when
      // it answered at all its answer is the better one - including its "no
      // subscription to manage", which the SDK cannot distinguish from a
      // failure.
      //
      // With ONE exception: a working link beats a "nothing found". If the
      // server saw nothing but the SDK handed back a real management URL, the
      // two disagree about whether a subscription exists, and the SDK only
      // ever produces that URL when one does. Showing it costs the customer
      // the email round trip; withholding it costs them the ability to cancel
      // at all. The server's STORE still travels, because it is the only
      // confirmed thing we have.
      if (server && (server.outcome === 'ok' || server.outcome === 'none')) {
        const url = safeLink(server.url) ?? (sdk.ok ? safeLink(sdk.managementUrl) : null)
        setState({
          belongsTo: userId,
          url,
          store: server.store,
          // eslint-disable-next-line i18next/no-literal-string -- outcome code
          outcome: url ? 'ok' : 'none',
          willRenew,
          isTrial,
        })
      } else {
        // The server could not answer, or answered `unconfigured`. It may
        // STILL have identified the store on its way to failing (a manageable
        // subscription it could not produce a link for), and that is the one
        // confirmed fact available here - throwing it away leaves a customer
        // with a platform-less "we could not check" when we know exactly
        // where their money goes.
        const store = server?.store ?? null
        if (sdk.ok) {
          const url = safeLink(sdk.managementUrl)
          setState({
            belongsTo: userId,
            url,
            store,
            // eslint-disable-next-line i18next/no-literal-string -- outcome code
            outcome: url ? 'ok' : 'none',
            willRenew,
            isTrial,
          })
        } else {
          setState({
            belongsTo: userId,
            url: null,
            store,
            // eslint-disable-next-line i18next/no-literal-string -- outcome code
            outcome: 'error',
            willRenew,
            isTrial,
          })
        }
      }
    })()

    return () => {
      cancelled = true
    }
  }, [userId, token])

  // Anything not stamped with the CURRENT user reads as unresolved, which is
  // what both consumers render nothing for.
  const fresh = userId !== null && state.belongsTo === userId
  const view = fresh ? state : UNRESOLVED
  return {
    url: view.url,
    resolved: fresh,
    outcome: view.outcome,
    store: view.store,
    willRenew: view.willRenew,
    isTrial: view.isTrial,
  }
}

interface Resolved {
  belongsTo: string | null
  url: string | null
  store: string | null
  outcome: ManagementOutcome
  willRenew: boolean | null
  isTrial: boolean | null
}

const UNRESOLVED: Resolved = {
  belongsTo: null,
  url: null,
  store: null,
  outcome: 'pending',
  willRenew: null,
  isTrial: null,
}

/**
 * Whether a value is a URL we are willing to put in an `href`.
 *
 * Both consumers render this as a link the customer is TOLD to click on the
 * account page, so a `javascript:` or `data:` value would be script execution
 * on the billing surface. The server applies the same rule to what it returns;
 * this covers the SDK path, which never passes through our server at all.
 *
 * https only, and it must parse with a host. No host allowlist: RevenueCat
 * legitimately returns its own portal, Apple's, and Google's depending on the
 * store, and pinning that list would break on the next store it supports.
 */
function safeLink(value: string | null): string | null {
  if (!value) return null
  try {
    const parsed = new URL(value)
    return parsed.protocol === 'https:' && parsed.host ? value : null
  } catch {
    return null
  }
}

/** Never rejects. Null means "no usable answer from the server". */
async function fetchServerManageUrl(
  token: string | null,
): Promise<ServerManageUrl | null> {
  if (!token) return null
  try {
    const body = await apiGet<ServerManageUrl>('/pro/manage-url', {
      bearerToken: token,
    })
    if (!body || typeof body !== 'object') return null
    return body
  } catch {
    return null
  }
}

interface SdkSummary {
  ok: boolean
  managementUrl: string | null
  willRenew: boolean | null
  isTrial: boolean | null
}

/** Never rejects. `ok: false` means the SDK could not be asked. */
async function fetchSdkSummary(userId: string): Promise<SdkSummary> {
  try {
    await configureRc(userId)
    const next = await getSubscriptionSummary()
    return {
      ok: true,
      managementUrl: next.managementUrl,
      willRenew: next.willRenew,
      isTrial: next.isTrial,
    }
  } catch {
    return { ok: false, managementUrl: null, willRenew: null, isTrial: null }
  }
}
